/* ─────────────────────────────────────────────────────────────────────────
   EB-SNOW · 黑五页的雪。只有引擎，不知道自己在哪个 section 里。

   用法：new window.EbSnow(host, { back, front, letters, pile })
     host     接收指针/触摸的那个 section（canvas 本身 pointer-events:none，
              按钮照样能点）
     back     画雪的主画布
     front    可选。少量失焦的大雪片画在这一层，叠在文字前面——景深从这来
     letters  可选。一组字母元素，雪会积在它们的顶上
     pile     可选。true = 雪落到底部堆成雪堆（收尾段）
     cap      可选。true = 宿主顶边画一道积雪（冬季层的页脚：雪积在页脚的上沿）
     interactive  默认 true。false = 不理鼠标和手指，只是背景（冬季层的小雪）
     halo     可选。函数，返回视口里浅色底从哪个 y 开始；那以下的雪片垫一圈很淡的深色，
              否则白雪落在冰蓝底上看不出轮廓（黑五页整页的雪）

   设计理由都在 references/black-friday/README.md，这里只记踩过的坑：

   · 积雪的高度不能用元素的 rect 顶边。行内元素的 rect 是字体的内容区，
     比大写字母的顶高出一截，雪会悬在半空。用 canvas 量同一个字体的
     fontBoundingBoxAscent − 'H' 的 actualBoundingBoxAscent，得到真实的字母顶。
   · 白雪落在白字上看不见。积雪底下先画一道带偏移的暗影当「缝」，再画雪。
   · 手机上大部分手势是竖向滚动，pointermove 在滚动开始那一刻就被
     pointercancel 掐掉了。所以触摸走 touchmove（passive，不拦滚动），
     滚动速度另外当作「上升气流」喂给雪。
   · 滚出视口就停帧；减弱动效（系统设置或后台 data-motion="off"）只画一帧静止的雪。
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  if (window.EbSnow) return;

  var DPR = Math.min(window.devicePixelRatio || 1, 2);
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.getAttribute('data-motion') === 'off';

  /* 颜色从 section 自己的 --color-accent 取，换主色主题时雪的冷光跟着换 */
  function rgb(str, fallback) {
    var s = (str || '').trim();
    var m = s.match(/^#([0-9a-f]{6})$/i);
    if (m) {
      var n = parseInt(m[1], 16);
      return [n >> 16 & 255, n >> 8 & 255, n & 255];
    }
    m = s.match(/rgba?\(([^)]+)\)/);
    if (m) return m[1].split(',').slice(0, 3).map(function (v) { return parseFloat(v); });
    return fallback;
  }

  function sprite(soft, tint) {
    var c = document.createElement('canvas'), s = 128;
    c.width = c.height = s;
    var g = c.getContext('2d');
    var grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    var t = tint.join(',');
    if (soft) {
      grd.addColorStop(0, 'rgba(243,241,245,.55)');
      grd.addColorStop(0.45, 'rgba(' + t + ',.16)');
      grd.addColorStop(1, 'rgba(' + t + ',0)');
    } else {
      grd.addColorStop(0, 'rgba(255,255,255,1)');
      grd.addColorStop(0.22, 'rgba(255,255,255,.95)');
      grd.addColorStop(0.32, 'rgba(' + t + ',.4)');
      grd.addColorStop(1, 'rgba(' + t + ',0)');
    }
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
    return c;
  }

  /* 浅色底上的雪垫的那一圈：不是描边，是一团很淡的暗影，远看雪片边缘自己就清楚了 */
  function shade() {
    var c = document.createElement('canvas'), s = 64;
    c.width = c.height = s;
    var g = c.getContext('2d');
    var grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grd.addColorStop(0, 'rgba(12,28,41,.22)');
    grd.addColorStop(0.5, 'rgba(12,28,41,.08)');
    grd.addColorStop(1, 'rgba(12,28,41,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, s, s);
    return c;
  }

  function Snow(host, opts) {
    this.host = host;
    this.back = opts.back;
    this.front = opts.front || null;
    this.letters = opts.letters || null;
    this.pile = !!opts.pile;
    this.cap = !!opts.cap;
    this.interactive = opts.interactive !== false;
    this.halo = opts.halo || null;
    this.bx = this.back.getContext('2d');
    this.fx = this.front ? this.front.getContext('2d') : null;
    this.flakes = [];
    this.ledges = [];
    this.heights = null;
    this.p = { x: -999, y: -999, vx: 0, vy: 0, t: 0, on: false };
    this.updraft = 0;
    this.density = opts.density == null ? 1 : opts.density;
    this.melting = false;
    this.running = false;
    this.visible = false;
    this.t = 0;

    this.tint = rgb(getComputedStyle(host).getPropertyValue('--color-accent'), [107, 165, 210]);
    this.SOFT = sprite(true, this.tint);
    this.SHARP = sprite(false, this.tint);
    if (this.halo) this.HALO = shade();

    this.bind();
    this.resize();
  }

  Snow.prototype.bind = function () {
    var self = this, host = this.host;
    function move(x, y) {
      var r = host.getBoundingClientRect(), nx = x - r.left, ny = y - r.top;
      var ts = performance.now(), dt = Math.max(16, ts - self.p.t);
      if (self.p.on) {
        self.p.vx = self.p.vx * 0.5 + ((nx - self.p.x) / dt * 1000) * 0.5;
        self.p.vy = self.p.vy * 0.5 + ((ny - self.p.y) / dt * 1000) * 0.5;
      }
      self.p.x = nx; self.p.y = ny; self.p.t = ts; self.p.on = true;
      if (self.onInteract) self.onInteract();
    }
    if (this.interactive) {
      host.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'mouse') move(e.clientX, e.clientY);
      });
      host.addEventListener('pointerleave', function () { self.p.on = false; });
      host.addEventListener('touchstart', function (e) {
        var t = e.touches[0];
        self.p.on = false;
        move(t.clientX, t.clientY);
        self.burst(self.p.x, self.p.y);
      }, { passive: true });
      host.addEventListener('touchmove', function (e) {
        var t = e.touches[0];
        move(t.clientX, t.clientY);
      }, { passive: true });
      host.addEventListener('touchend', function () {
        setTimeout(function () { self.p.on = false; }, 120);
      }, { passive: true });
    }

    var ly = window.scrollY, lt = performance.now();
    window.addEventListener('scroll', function () {
      var ts = performance.now(), v = (window.scrollY - ly) / Math.max(16, ts - lt) * 1000;
      ly = window.scrollY; lt = ts;
      self.updraft = Math.max(-900, Math.min(900, v));
    }, { passive: true });

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { self.resize(); }, 120);
    });

    new IntersectionObserver(function (es) {
      self.visible = es[0].isIntersecting;
      if (self.visible) self.start();
    }).observe(host);

    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.measure(); });
  };

  Snow.prototype.resize = function () {
    var r = this.host.getBoundingClientRect();
    this.w = r.width; this.h = r.height;
    [this.back, this.front].forEach(function (c) {
      if (!c) return;
      c.width = Math.round(this.w * DPR);
      c.height = Math.round(this.h * DPR);
    }, this);
    this.bx.setTransform(DPR, 0, 0, DPR, 0, 0);
    if (this.fx) this.fx.setTransform(DPR, 0, 0, DPR, 0, 0);
    this.max = Math.round(Math.min(420, this.w * this.h / (this.w < 600 ? 3600 : 2600)));
    if (this.pile) {
      var n = Math.ceil(this.w / 4) + 1, old = this.heights, H = new Float32Array(n);
      for (var i = 0; i < n; i++) H[i] = old && i < old.length ? old[i] : 14 + Math.sin(i / 9) * 5 + Math.sin(i / 23) * 7;
      this.heights = H;
    }
    this.measure();
    if (!this.flakes.length) {
      for (var k = 0; k < this.max * this.density; k++) this.flakes.push(this.spawn(true));
    }
    if (reduce) this.still();
  };

  Snow.prototype.measure = function () {
    if (!this.letters || !this.letters.length) { this.ledges = []; return; }
    var src = this.letters[0];
    var rects = Array.prototype.map.call(this.letters, function (el) { return el.getBoundingClientRect(); });
    var hr = this.host.getBoundingClientRect(), ctx = this.bx;
    var cs = getComputedStyle(src);
    ctx.font = cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
    var m = ctx.measureText('H'), fa = m.fontBoundingBoxAscent, capA = m.actualBoundingBoxAscent;
    var old = this.ledges;
    this.ledges = rects.map(function (r, i) {
      var cap = r.top - hr.top + fa - capA;
      var inset = r.width * 0.06, x0 = r.left - hr.left + inset, x1 = r.right - hr.left - inset;
      var n = Math.max(2, Math.round((x1 - x0) / 3));
      var mx = Math.max(5, r.height * 0.065);
      var col;
      if (old[i] && old[i].n === n) col = old[i].col;
      else {
        /* 打开页面时字上已经有一层薄雪：昨夜下过 */
        col = new Float32Array(n);
        for (var c = 0; c < n; c++) col[c] = mx * 0.35 * Math.pow(Math.sin(Math.PI * (c + 0.5) / n), 0.5) * (0.7 + Math.random() * 0.3);
      }
      return { x0: x0, x1: x1, y: cap, col: col, n: n, max: mx };
    });
  };

  Snow.prototype.spawn = function (anywhere) {
    var z = Math.pow(Math.random(), 1.6);
    var near = !!this.front && Math.random() < 0.02;
    return {
      x: Math.random() * this.w,
      y: anywhere ? Math.random() * this.h : -20 - Math.random() * 60,
      z: near ? 1 : z, near: near,
      r: near ? 7 + Math.random() * 7 : 1 + z * 3.4,
      vx: 0, vy: 0, ph: Math.random() * 6.28, f: 0
    };
  };

  Snow.prototype.burst = function (x, y) {
    if (reduce) return;
    for (var i = 0; i < 14; i++) {
      var a = Math.random() * 6.28, s = 120 + Math.random() * 260, f = this.spawn(false);
      f.x = x; f.y = y; f.z = 0.5 + Math.random() * 0.4; f.near = false; f.r = 2 + Math.random() * 2.5;
      f.vx = Math.cos(a) * s; f.vy = Math.sin(a) * s - 60; f.f = 1;
      this.flakes.push(f);
    }
  };

  Snow.prototype.start = function () {
    if (reduce || this.running) return;
    this.running = true;
    var self = this, last = performance.now();
    function tick(ts) {
      if (!self.visible) { self.running = false; return; }
      var dt = Math.min(0.05, (ts - last) / 1000);
      last = ts;
      self.step(dt);
      self.draw();
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  };

  Snow.prototype.pileMax = function () { return this.w < 600 ? 46 : 78; };

  Snow.prototype.step = function (dt) {
    this.t += dt;
    var p = this.p, R = this.w < 600 ? 130 : 170, speed = Math.hypot(p.vx, p.vy);
    p.vx *= Math.pow(0.02, dt); p.vy *= Math.pow(0.02, dt);
    this.updraft *= Math.pow(0.04, dt);
    var wind = Math.sin(this.t * 0.23) * 14 + Math.sin(this.t * 0.07) * 10;
    var target = this.melting ? 0 : Math.round(this.max * this.density);
    var alive = 0, i, c, L;

    for (i = this.flakes.length - 1; i >= 0; i--) {
      var f = this.flakes[i];
      var fall = f.near ? 34 : 12 + f.z * 58;
      var focus = 0;
      if (p.on && !f.near) {
        var dx = f.x - p.x, dy = f.y - p.y, d = Math.hypot(dx, dy);
        if (d < R && d > 0.01) {
          var k = 1 - d / R;
          focus = k;
          f.vx += p.vx * k * k * (0.9 + f.z) * dt * 2.2;
          f.vy += p.vy * k * k * (0.9 + f.z) * dt * 2.2;
          /* 光标停着时雪也绕着它慢慢转——参考图里那种「聚拢」 */
          var sw = (40 + speed * 0.15) * k * dt * (f.z + 0.3);
          f.vx += -dy / d * sw; f.vy += dx / d * sw;
        }
      }
      f.f += (focus - f.f) * Math.min(1, dt * (focus > f.f ? 10 : 2.2));
      f.vx *= Math.pow(0.25, dt); f.vy *= Math.pow(0.25, dt);
      var sway = Math.sin(this.t * (0.6 + f.z * 0.5) + f.ph) * (6 + f.z * 16);
      var py = f.y;
      f.x += (f.vx + wind * (0.3 + f.z) + sway * 0.6) * dt;
      f.y += (f.vy + fall - this.updraft * (0.12 + f.z * 0.25)) * dt;

      /* 只有中景的雪会落在字上：远景在字后面，近景在字前面 */
      if (this.ledges.length && f.z > 0.18 && f.z < 0.9 && !f.near && !this.melting && f.vy < 80) {
        for (var j = 0; j < this.ledges.length; j++) {
          L = this.ledges[j];
          if (f.x <= L.x0 || f.x >= L.x1) continue;
          c = Math.floor((f.x - L.x0) / (L.x1 - L.x0) * L.n);
          var top = L.y - L.col[c];
          if (py <= top && f.y >= top) {
            if (L.col[c] < L.max) {
              L.col[c] += 2.6;
              if (c > 0) L.col[c - 1] += 1.6;
              if (c < L.n - 1) L.col[c + 1] += 1.6;
              if (c > 1) L.col[c - 2] += 0.6;
              if (c < L.n - 2) L.col[c + 2] += 0.6;
            }
            f.dead = true;
            break;
          }
        }
      }

      if (this.heights && !f.near && !f.dead) {
        var H = this.heights;
        c = Math.max(0, Math.min(H.length - 1, Math.round(f.x / 4)));
        if (f.y >= this.h - H[c]) {
          if (!this.melting) {
            H[c] = Math.min(this.pileMax(), H[c] + 1.1 + f.z * 1.4);
            if (c > 0) H[c - 1] += 0.12;
            if (c < H.length - 1) H[c + 1] += 0.12;
          }
          f.dead = true;
        }
      }

      if (f.dead || f.y > this.h + 40 || f.x < -60 || f.x > this.w + 60) {
        this.flakes.splice(i, 1);
        continue;
      }
      alive++;
    }
    for (i = alive; i < target; i++) if (Math.random() < 0.5) this.flakes.push(this.spawn(false));

    /* 快速划过积雪：把它吹起来 */
    if (p.on && speed > 380) {
      for (var q = 0; q < this.ledges.length; q++) {
        L = this.ledges[q];
        if (p.y < L.y - 60 || p.y > L.y + 30 || p.x < L.x0 - 30 || p.x > L.x1 + 30) continue;
        for (c = 0; c < L.n; c++) {
          var x = L.x0 + (c + 0.5) / L.n * (L.x1 - L.x0);
          if (Math.abs(x - p.x) < 40 && L.col[c] > 0.5) {
            var take = L.col[c] * 0.6;
            L.col[c] -= take;
            if (Math.random() < take * 0.5) {
              var nf = this.spawn(false);
              nf.x = x; nf.y = L.y - 2; nf.z = 0.55; nf.near = false; nf.r = 2.2; nf.f = 1;
              nf.vx = p.vx * 0.45 + (Math.random() - 0.5) * 80; nf.vy = -80 - Math.random() * 120;
              this.flakes.push(nf);
            }
          }
        }
      }
    }

    /* 积雪慢慢塌成圆顶，而不是一根根柱子 */
    for (q = 0; q < this.ledges.length; q++) {
      var C = this.ledges[q].col, n = this.ledges[q].n;
      for (c = 1; c < n - 1; c++) C[c] += ((C[c - 1] + C[c + 1]) / 2 - C[c]) * Math.min(1, dt * 3);
      C[0] *= 0.98; C[n - 1] *= 0.98;
      if (this.melting) for (c = 0; c < n; c++) C[c] = Math.max(0, C[c] - dt * 2);
    }

    if (this.heights) {
      var Hh = this.heights, mx = this.pileMax();
      for (i = 1; i < Hh.length - 1; i++) Hh[i] += ((Hh[i - 1] + Hh[i + 1]) / 2 - Hh[i]) * Math.min(1, dt * 1.5);
      if (this.melting) for (i = 0; i < Hh.length; i++) Hh[i] = Math.max(0, Hh[i] - dt * 6);
      /* 在雪堆上划过会扒开一道 */
      if (p.on && p.y > this.h - mx - 60) {
        for (i = 0; i < Hh.length; i++) {
          var dd = Math.abs(i * 4 - p.x);
          if (dd < 46) Hh[i] = Math.max(3, Hh[i] - (1 - dd / 46) * dt * (60 + speed * 0.08));
        }
      }
    }
  };

  Snow.prototype.draw = function () {
    var b = this.bx, fx = this.fx, i, f, s;
    b.clearRect(0, 0, this.w, this.h);
    if (fx) fx.clearRect(0, 0, this.w, this.h);
    var hy = this.halo ? this.halo() : Infinity;
    for (i = 0; i < this.flakes.length; i++) {
      f = this.flakes[i];
      var ctx = f.near && fx ? fx : b;
      var onLight = f.y > hy && !f.near;
      if (onLight) {
        var hs = f.r * 3;
        ctx.globalAlpha = 0.3 + f.z * 0.3;
        ctx.drawImage(this.HALO, f.x - hs / 2, f.y - hs / 2, hs, hs);
      }
      if (f.near) {
        s = f.r * 4;
        ctx.globalAlpha = 0.16;
        ctx.drawImage(this.SOFT, f.x - s / 2, f.y - s / 2, s, s);
        continue;
      }
      var base = 0.22 + f.z * 0.55;
      s = f.r * (4 + f.f * 1.2) * (1 + (1 - f.z) * 0.4);
      if (f.f < 0.98) {
        ctx.globalAlpha = base * (1 - f.f);
        ctx.drawImage(this.SOFT, f.x - s / 2, f.y - s / 2, s, s);
      }
      if (f.f > 0.02 || f.z > 0.75) {
        var a = f.z > 0.75 ? Math.max(f.f, 0.6) : f.f;
        var ss = f.r * (2.4 + f.f * 3.2);
        ctx.globalAlpha = Math.min(1, a * (0.5 + f.z * 0.6));
        ctx.drawImage(this.SHARP, f.x - ss / 2, f.y - ss / 2, ss, ss);
      }
      /* 浅底上光晕式的雪几乎是透明的，补一个实心白芯，雪才是白的而不是一圈灰 */
      if (onLight) {
        ctx.globalAlpha = 0.75 + f.z * 0.25;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(f.x, f.y, Math.max(0.9, f.r * 0.75), 0, 6.2832);
        ctx.fill();
      }
    }
    b.globalAlpha = 1;
    if (fx) fx.globalAlpha = 1;
    var t = this.tint.join(',');

    if (this.p.on && this.letters) {
      var R = this.w < 600 ? 130 : 170;
      var g = b.createRadialGradient(this.p.x, this.p.y, 0, this.p.x, this.p.y, R);
      g.addColorStop(0, 'rgba(' + t + ',.10)');
      g.addColorStop(1, 'rgba(' + t + ',0)');
      b.fillStyle = g;
      b.fillRect(this.p.x - R, this.p.y - R, R * 2, R * 2);
    }

    for (i = 0; i < this.ledges.length; i++) {
      var L = this.ledges[i], any = false, c;
      for (c = 0; c < L.n; c++) if (L.col[c] > 0.3) { any = true; break; }
      if (!any) continue;
      b.beginPath();
      b.moveTo(L.x0, L.y + 1.5);
      for (c = 0; c < L.n; c++) b.lineTo(L.x0 + (c + 0.5) / L.n * (L.x1 - L.x0), L.y - Math.min(L.max, L.col[c]));
      b.lineTo(L.x1, L.y + 1.5);
      b.closePath();
      b.shadowColor = 'rgba(7,7,7,.9)'; b.shadowBlur = 3; b.shadowOffsetY = 2.5;
      b.fillStyle = '#fff'; b.fill();
      b.shadowOffsetY = 0; b.shadowColor = 'rgba(' + t + ',.9)'; b.shadowBlur = 8;
      b.fillStyle = 'rgb(236,244,251)'; b.fill();
      b.shadowBlur = 0;
    }

    /* 顶边积雪：不随时间变，只是一道起伏的雪檐。底下一道暗影，
       否则在浅色页面和深色页脚之间它会被看成页脚边缘被切了一刀 */
    if (this.cap) {
      b.beginPath();
      b.moveTo(0, 0);
      for (var x = 0; x <= this.w + 6; x += 6) {
        b.lineTo(x, 7 + Math.sin(x / 37) * 3 + Math.sin(x / 91 + 1) * 4 + Math.sin(x / 13) * 1.2);
      }
      b.lineTo(this.w, 0);
      b.closePath();
      b.fillStyle = 'rgb(222,233,244)';
      b.shadowColor = 'rgba(0,0,0,.65)'; b.shadowOffsetY = 3; b.shadowBlur = 6;
      b.fill();
      b.shadowOffsetY = 0; b.shadowBlur = 0;
    }

    if (this.heights) {
      var H = this.heights;
      b.beginPath();
      b.moveTo(0, this.h);
      for (i = 0; i < H.length; i++) b.lineTo(i * 4, this.h - H[i]);
      b.lineTo(this.w, this.h);
      b.closePath();
      /* 白雪，顶边底下压一道暗缝——和字母上的积雪同一个做法。原来往下渐变成主色，
         放在冰蓝底上像一摊蓝色的东西，不像雪 */
      b.shadowColor = 'rgba(7,7,7,.55)'; b.shadowBlur = 4; b.shadowOffsetY = 3;
      b.fillStyle = '#fff';
      b.fill();
      b.shadowOffsetY = 0; b.shadowBlur = 0;
      var pg = b.createLinearGradient(0, this.h - this.pileMax(), 0, this.h);
      pg.addColorStop(0, 'rgb(250,251,253)');
      pg.addColorStop(1, 'rgb(232,238,245)');
      b.fillStyle = pg;
      b.fill();
    }
  };

  Snow.prototype.still = function () {
    for (var i = 0; i < 4; i++) this.step(0.03);
    this.draw();
  };

  Snow.reduced = reduce;
  window.EbSnow = Snow;
})();
